import {
    LambdaHistoryItem,
    LambdaLayerDescriptor,
    LambdaOperation,
    LambdaOperationField,
    LambdaOperationMetadata,
} from '@api/lambdaOperationsApi';
import { MetadataPropertyEntity } from '@api/entities/replancity_MetadataProperty';
import { EntityMapLayer } from '@api/entities/replancity_MapLayer';
import { MapboxLayerType } from '@api/enums/enums';

export const LAMBDA_LAYERS_GROUP = 'Lambda operations';

const COMPLETED_STATES = new Set(['COMPLETE', 'COMPLETED', 'FINISHED', 'SUCCESS']);
const RUNNING_STATES = new Set([
    'IN_PROGRESS',
    'RUNNING',
    'PENDING',
    'QUEUE',
    'QUEUED',
    'RESTARTING',
    'PREPARE_MODEL',
]);
const FAILED_STATES = new Set(['ERROR', 'FAILED', 'TIMEOUT']);

const padDatePart = (value: number): string => String(value).padStart(2, '0');

export const getLambdaOperationId = (operation: LambdaOperation): string =>
    String(operation.operationId || operation.code || operation.type || operation.id || '');

export const getLambdaOperationTitle = (operation: LambdaOperation): string =>
    String(
        operation.printedName ||
            operation.title ||
            operation.name ||
            operation.operationId ||
            operation.type ||
            ''
    );

export const isLambdaTaskCompleted = (state?: string): boolean =>
    COMPLETED_STATES.has(String(state || '').toUpperCase());

export const isLambdaTaskRunning = (state?: string): boolean =>
    RUNNING_STATES.has(String(state || '').toUpperCase());

export const isLambdaTaskFailed = (state?: string): boolean =>
    FAILED_STATES.has(String(state || '').toUpperCase());

export const formatLambdaHistoryDateTime = (...dates: Array<string | undefined>): string => {
    const validDate = dates.find(date => {
        if (!date || date.toLowerCase() === 'invalid date') {
            return false;
        }

        return !Number.isNaN(Date.parse(date));
    });

    if (!validDate) {
        return '';
    }

    const date = new Date(validDate);
    return [
        `${date.getFullYear()}-${padDatePart(date.getMonth() + 1)}-${padDatePart(date.getDate())}`,
        `${padDatePart(date.getHours())}:${padDatePart(date.getMinutes())}:${padDatePart(date.getSeconds())}`,
    ].join(' ');
};

export const parseLambdaParams = (
    params?: string | Record<string, unknown>
): Record<string, unknown> => {
    if (!params) {
        return {};
    }

    if (typeof params === 'string') {
        let parsed: unknown;
        try {
            parsed = JSON.parse(params);
        } catch {
            parsed = {};
        }
        return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    }

    return params;
};

export const getLambdaParamsSummary = (params?: string | Record<string, unknown>): string => {
    const parsedParams = parseLambdaParams(params);
    const entries = Object.entries(parsedParams).filter(
        ([, value]) => value !== '' && value !== null && value !== undefined
    );

    if (!entries.length) {
        return '';
    }

    return entries
        .slice(0, 3)
        .map(([key, value]) => {
            const normalizedValue = Array.isArray(value) ? value.join(', ') : String(value);
            return `${key}: ${normalizedValue}`;
        })
        .join('; ');
};

const normalizeOptions = (options: LambdaOperationField['options']) => {
    if (!options) {
        return [];
    }

    if (Array.isArray(options)) {
        return options.map(option => ({
            value: option.value,
            caption:
                option.caption ||
                option.label ||
                option.title ||
                option.name ||
                String(option.value),
        }));
    }

    return Object.entries(options).map(([value, caption]) => ({
        value,
        caption: String(caption),
    }));
};

const getMetadataOptions = (metadata: MetadataPropertyEntity) =>
    (metadata.options || []).map(({ id, propertyName, printedName }) => ({
        value: metadata.type === 'Enum' ? propertyName : id,
        caption: printedName || propertyName,
    }));

const metadataPropertyToLambdaField = (metadata: MetadataPropertyEntity): LambdaOperationField => {
    const options = getMetadataOptions(metadata);

    return {
        id: metadata.id,
        name: metadata.name,
        label: metadata.printedName,
        type: metadata.type,
        required: metadata.mandatoryIf === 'true',
        defaultValue: metadata.defaultValue ?? options[0]?.value ?? '',
        options,
        validationRegex: metadata.validationRegex,
    };
};

export const normalizeLambdaMetadata = (
    metadata: LambdaOperationMetadata | MetadataPropertyEntity[] | LambdaOperationField[]
): LambdaOperationMetadata => {
    if (Array.isArray(metadata)) {
        return {
            fields: metadata
                .filter(field => !('showIf' in field) || (!!field.showIf && field.showIf !== 'false'))
                .sort((left, right) => {
                    const leftOrder = 'order' in left ? Number(left.order) : 0;
                    const rightOrder = 'order' in right ? Number(right.order) : 0;
                    return leftOrder - rightOrder;
                })
                .map(field =>
                    'printedName' in field || 'mandatory' in field
                        ? metadataPropertyToLambdaField(field as MetadataPropertyEntity)
                        : (field as LambdaOperationField)
                ),
        };
    }

    return {
        ...metadata,
        fields: (metadata.fields || []).map(field => ({
            ...field,
            name: field.name || field.key || field.id || '',
            options: normalizeOptions(field.options),
        })),
    };
};

export const getLambdaFieldLabel = (field: LambdaOperationField): string =>
    String(field.label || field.title || field.printedName || field.name);

export const getLambdaFieldDefaultValue = (field: LambdaOperationField): unknown =>
    field.defaultValue ?? field.value ?? (normalizeFieldType(field.type) === 'boolean' ? false : '');

export const normalizeFieldType = (type?: string): string => {
    const normalized = String(type || 'string').toLowerCase();

    if (['integer', 'double', 'float', 'long', 'number'].includes(normalized)) {
        return 'number';
    }
    if (['boolean', 'bool'].includes(normalized)) {
        return 'boolean';
    }
    if (['enum', 'select', 'reference'].includes(normalized)) {
        return 'select';
    }
    if (['multiselect', 'multi-select', 'multiple', 'set'].includes(normalized)) {
        return 'multi-select';
    }
    if (['localtime', 'time', 'departure', 'interval'].includes(normalized)) {
        return 'time';
    }
    if (['localdate', 'date'].includes(normalized)) {
        return 'date';
    }
    if (['localdatetime', 'datetime', 'date-time'].includes(normalized)) {
        return 'datetime-local';
    }
    if (['point', 'linestring', 'line-string', 'polygon', 'geometry'].includes(normalized)) {
        return 'geometry';
    }

    return 'string';
};

export const canOpenLambdaDashboard = (item: LambdaHistoryItem): boolean =>
    !!item.containsDashboard && !!getLambdaDashboardTarget(item, '');

export const getLambdaDashboardTarget = (item: LambdaHistoryItem, projectId: string): string => {
    const directTarget =
        item.dashboardUrl ||
        item.dashboardLink ||
        item.dashboardPath ||
        item.dashboard?.url ||
        item.dashboard?.link ||
        item.dashboard?.path;

    if (directTarget) {
        return directTarget;
    }

    const dashboardId = item.dashboardId || item.dashboard?.id;
    if (projectId && dashboardId && item.id) {
        return `/${projectId}/dashboard/${item.id}/${dashboardId}/sdashboard`;
    }

    return '';
};

export const openLambdaDashboard = (item: LambdaHistoryItem, projectId: string): void => {
    const target = getLambdaDashboardTarget(item, projectId);
    if (!target) {
        return;
    }

    window.open(target, '_blank', 'noopener,noreferrer');
};

export const toLambdaFallbackLayer = (item: LambdaHistoryItem): EntityMapLayer | null => {
    if (!item.lambdaLayerId) {
        return null;
    }

    return {
        _entityName: 'replancity_ReadOnlyLayerEntity',
        id: item.lambdaLayerId,
        viewName: item.name || item.type || item.lambdaLayerId,
        entityName: 'replancity_LambdaOperation',
        displayName: item.name || item.type || item.lambdaLayerId,
        needAlgorithmId: false,
        dayTimeDependent: false,
        clusterize: false,
        clickable: false,
        updateOnFeatureSelection: false,
        calculationState: item.state,
        showByDefault: true,
        type: MapboxLayerType.POLYGON,
        order: 1000,
        opacity: 0.8,
        group: LAMBDA_LAYERS_GROUP,
        hasBalloon: false,
        queryable: true,
    };
};

export const normalizeLambdaLayers = (
    layers: LambdaLayerDescriptor[],
    item: LambdaHistoryItem
): LambdaLayerDescriptor[] => {
    const normalizedLayers = layers.filter(Boolean).map((layer, index) => {
        const { usageType, relatedLayerId, ...layerData } = layer;

        return {
            ...layerData,
            id: layer.id || item.lambdaLayerId || `${item.id}_${index}`,
            displayName: layer.displayName || layer.viewName || item.name || item.type || item.id,
            viewName: layer.viewName || layer.displayName || item.name || item.type || item.id,
            entityName: layer.entityName || 'replancity_LambdaOperation',
            _entityName: layer._entityName || 'replancity_ReadOnlyLayerEntity',
            group: layer.group || LAMBDA_LAYERS_GROUP,
            showByDefault: true,
            order: layer.order ?? 1000,
            opacity: layer.opacity ?? 0.8,
            needAlgorithmId: layer.needAlgorithmId ?? false,
            dayTimeDependent: layer.dayTimeDependent ?? false,
            clickable: layer.clickable ?? false,
            updateOnFeatureSelection: layer.updateOnFeatureSelection ?? false,
            hasBalloon: layer.hasBalloon ?? false,
            type: layer.type || MapboxLayerType.POLYGON,
            usageType: 'data' as const,
            queryable: layer.featureCollection ? false : (layer.queryable ?? true),
        };
    });

    if (normalizedLayers.length) {
        return normalizedLayers;
    }

    const fallbackLayer = toLambdaFallbackLayer(item);
    return fallbackLayer ? [fallbackLayer] : [];
};
